/**
 * Parts Master upload dialog (drag-and-drop Excel upload + result panel).
 *
 * Extracted from `PartsMaster.tsx` to keep the page component under the
 * 500-line cap. Pure presentation — actual upload + state lives in the
 * parent.
 */
import { CheckCircle, FileSpreadsheet, Loader2, Upload, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import type { PartUploadResponse } from '../../lib/api/types/parts';

interface PartsMasterUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadFile: File | null;
  onFileSelected: (file: File | null) => void;
  uploading: boolean;
  uploadResult: PartUploadResponse | null;
  onUpload: () => void;
  onDone: () => void;
}

/**
 * Render the upload modal. Drag-and-drop and the result-summary states are
 * both handled here; the parent owns the file state and triggers the upload.
 */
export default function PartsMasterUploadDialog({
  open,
  onOpenChange,
  uploadFile,
  onFileSelected,
  uploading,
  uploadResult,
  onUpload,
  onDone,
}: PartsMasterUploadDialogProps) {
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelected(e.dataTransfer.files[0]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Parts Master Data</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!uploadResult ? (
            <>
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center transition-colors border-muted-foreground/25"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm font-medium">
                  {uploadFile ? uploadFile.name : 'Drag & drop your Excel file here'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports .xlsx and .xls files
                </p>
                <label className="mt-3 inline-block">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) onFileSelected(e.target.files[0]);
                    }}
                  />
                  <Button variant="outline" size="sm" asChild>
                    <span>Browse Files</span>
                  </Button>
                </label>
              </div>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-xs font-medium mb-1">Expected columns:</p>
                <p className="text-xs text-muted-foreground">
                  Item No. (required), Item Description, Group Name, Number (group code), UOM
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {uploadResult.error_count === 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-amber-500" />
                )}
                <span className="font-medium">Upload Complete</span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded p-2 text-center">
                  <div className="text-lg font-bold">{uploadResult.total_rows.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">Total Rows</div>
                </div>
                <div className="bg-green-50 dark:bg-green-950/20 rounded p-2 text-center">
                  <div className="text-lg font-bold text-green-600">
                    {uploadResult.created.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Created</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/20 rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {uploadResult.updated.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Updated</div>
                </div>
              </div>

              {uploadResult.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/20 rounded p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-red-600 mb-1">
                    {uploadResult.error_count} error(s):
                  </p>
                  {uploadResult.errors.slice(0, 10).map((err, i) => (
                    <p key={i} className="text-xs text-red-500">
                      Row {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!uploadResult ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={onUpload} disabled={!uploadFile || uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={onDone}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
